import React from 'react';
import { brandCategories, clientPartners } from '../mock';

// Typographic wordmarks (original text-based renderings, not brand logos).
function Wordmark({ name }) {
  const styles = {
    'T.J. Maxx': { font: 'Cormorant Garamond, serif', weight: 700, size: 26, letter: '-0.01em', italic: true },
    'Marshalls': { font: 'Inter, sans-serif', weight: 800, size: 24, letter: '-0.01em', italic: false },
    'HomeGoods': { font: 'Inter, sans-serif', weight: 800, size: 22, letter: '-0.015em', italic: false },
    'Sierra': { font: 'Cormorant Garamond, serif', weight: 600, size: 28, letter: '0.02em', italic: false },
    'Winners': { font: 'Inter, sans-serif', weight: 700, size: 24, letter: '0.06em', italic: false },
    'Burlington Stores': { font: 'Cormorant Garamond, serif', weight: 500, size: 22, letter: '0.02em', italic: false },
    'Ross Stores': { font: 'Inter, sans-serif', weight: 800, size: 26, letter: '-0.02em', italic: false },
    'Costco Wholesale': { font: 'Inter, sans-serif', weight: 800, size: 22, letter: '-0.01em', italic: false },
  };
  const s = styles[name] || { font: 'Inter, sans-serif', weight: 700, size: 22, letter: '0', italic: false };
  return (
    <div style={{
      fontFamily: s.font,
      fontWeight: s.weight,
      fontSize: s.size,
      letterSpacing: s.letter,
      fontStyle: s.italic ? 'italic' : 'normal',
      color: 'var(--sm-navy)',
      lineHeight: 1,
      whiteSpace: 'nowrap',
    }}>
      {name}
    </div>
  );
}

export default function NetworkSection() {
  return (
    <section className="section" style={{ background: 'var(--sm-ivory)' }}>
      <div className="container-x">
        {/* Brands */}
        <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 72px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            <span className="hairline" />
            <span className="overline">Brand Categories</span>
            <span className="hairline" />
          </div>
          <h2 className="serif" style={{ fontSize: 'clamp(32px, 4vw, 52px)', lineHeight: 1.1, color: 'var(--sm-navy)', margin: 0 }}>
            A curated network across three commercial verticals.
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }} className="brand-grid">
          {brandCategories.map((cat, idx) => (
            <div key={cat.title} style={{ padding: 40, background: 'var(--sm-cream)', border: '1px solid var(--sm-line)', minHeight: 320, display: 'flex', flexDirection: 'column', transition: 'transform 400ms cubic-bezier(.2,.7,.2,1), box-shadow 400ms ease' }}
                 onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 24px 60px -30px rgba(11,30,63,0.35)'; }}
                 onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
              <span style={{ fontSize: 11, letterSpacing: '0.28em', color: 'var(--sm-gold)' }}>0{idx + 1}</span>
              <h3 className="serif" style={{ fontSize: 26, color: 'var(--sm-navy)', marginTop: 16, marginBottom: 24 }}>{cat.title}</h3>
              <div style={{ flex: 1 }}>
                {cat.items.map((item) => (
                  <div key={item} style={{ fontSize: 14, color: 'var(--sm-muted)', padding: '10px 0', borderTop: '1px solid var(--sm-line)' }}>{item}</div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Key Accounts */}
        <div style={{ marginTop: 128 }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <span className="hairline" />
              <span className="overline">Key Accounts</span>
              <span className="hairline" />
            </div>
            <h2 className="serif" style={{ fontSize: 'clamp(30px, 3.6vw, 46px)', lineHeight: 1.15, color: 'var(--sm-navy)', margin: 0, maxWidth: 780, marginInline: 'auto' }}>
              Trusted by leading North American retailers.
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            border: '1px solid var(--sm-line)',
            background: 'var(--sm-ivory)',
          }} className="kg-grid">
            {clientPartners.map((b, i) => {
              const isLastCol = (i + 1) % 4 === 0;
              const isLastRow = i >= clientPartners.length - 4;
              return (
                <div key={b.name}
                     style={{
                       padding: '52px 20px',
                       borderRight: !isLastCol ? '1px solid var(--sm-line)' : 'none',
                       borderBottom: !isLastRow ? '1px solid var(--sm-line)' : 'none',
                       textAlign: 'center',
                       display: 'flex',
                       flexDirection: 'column',
                       alignItems: 'center',
                       justifyContent: 'center',
                       gap: 14,
                       minHeight: 160,
                       transition: 'background 220ms ease, transform 300ms ease',
                       cursor: 'default',
                     }}
                     onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sm-cream)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                     onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--sm-ivory)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Wordmark name={b.name} />
                  <div style={{ fontSize: 10, letterSpacing: '0.26em', textTransform: 'uppercase', color: 'var(--sm-muted)' }}>{b.tag}</div>
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', marginTop: 32, fontSize: 12, color: 'var(--sm-muted)', fontStyle: 'italic' }}>
            Retailer names and marks are property of their respective owners.
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .brand-grid { grid-template-columns: 1fr !important; }
          .kg-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 520px) {
          .kg-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
